import Image from "next/image";
import Link from "next/link";
import { BadgeCheck } from "lucide-react";

import { sanitizeUrl } from "@/lib/helpers";
import { Countdown } from "./live";

import "./sections.css";

/**
 * THE CATALOGUE TILES, SHARED.
 *
 * These were written inline in `HomeV2.tsx`, which was fine while the home
 * shelves were their only reader. The artist profile needs the same collection,
 * gallery and auction cards in a grid, and a second copy of each is how two
 * cards for one object start to drift. Home and the profile now render these.
 *
 * No "use client": they are presentational and render on the server for Home.
 * A client grid can import them too — the only client piece is `Countdown`.
 */

export const TILE_PLACEHOLDER = "/sampleNft1.png";

/**
 * ONE SPELLING PER CHAIN, EVERYWHERE.
 *
 * The API says "matic" on a collection, "polygon" on an activity row, "eth"
 * on a currency and "ethereum" on an NFT — four spellings across four
 * endpoints for two chains, and the page was printing whichever one it got.
 * The rule is: a full name is Title Case and spelled the way the chain spells
 * itself, a ticker is upper case. Anything unrecognised falls through
 * upper-cased rather than being dropped, so a new chain shows up as a ticker
 * instead of vanishing.
 */
const CHAIN_NAME: Record<string, string> = {
  matic: "Polygon",
  polygon: "Polygon",
  ethereum: "Ethereum",
  eth: "Ethereum",
};
const CHAIN_FA: Record<string, string> = {
  matic: "پالیگان",
  polygon: "پالیگان",
  ethereum: "اتریوم",
  eth: "اتریوم",
};

/** The normalised key the chain-colour tokens are keyed by (see `v2.css`). */
export const chainKey = (raw: unknown) => String(raw ?? "").trim().toLowerCase();

export function chainName(raw: unknown, isFa: boolean): string {
  const k = String(raw ?? "").trim().toLowerCase();
  if (!k) return "";
  if (isFa && CHAIN_FA[k]) return CHAIN_FA[k];
  return CHAIN_NAME[k] ?? k.toUpperCase();
}

/** A currency ticker is always upper case: eth → ETH, usdt → USDT. */
export const ticker = (raw: unknown) => String(raw ?? "").trim().toUpperCase();

/**
 * The backend ships a collection's Persian name inside `meta[0]` as a JSON
 * *string*, not an object — `meta: ["{\"fa_name\":\"…\",…}"]`. Parsing it is
 * the only way to show a Persian visitor the Persian name of a collection
 * whose `name` field is Latin, which is most of them.
 */
export function faName(meta: unknown): string | null {
  if (!Array.isArray(meta) || typeof meta[0] !== "string") return null;
  try {
    const parsed = JSON.parse(meta[0]) as { fa_name?: unknown };
    const v = parsed?.fa_name;
    return typeof v === "string" && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

/**
 * An account's avatar URL, or null.
 *
 * `profile` is an OBJECT on users who have set one and an empty ARRAY on users
 * who have not — `"profile": []` — so `user.profile?.cover` is not enough on
 * its own; `[].cover` is `undefined` rather than a type error, but the array
 * case is the common one and worth naming. Gallery owners carry a flat
 * `avatar` instead of a `profile` object, so both shapes are read here.
 */
export function avatarOf(u: unknown): string | null {
  if (!u || typeof u !== "object") return null;
  const o = u as { avatar?: unknown; profile?: unknown };
  if (typeof o.avatar === "string" && o.avatar) return sanitizeUrl(o.avatar) || null;
  const p = o.profile;
  if (p && typeof p === "object" && !Array.isArray(p)) {
    // `profile.avatar` where a list sends it (the auctions do), else the profile picture `cover`.
    const { avatar, cover } = p as { avatar?: unknown; cover?: unknown };
    if (typeof avatar === "string" && avatar) return sanitizeUrl(avatar) || null;
    if (typeof cover === "string" && cover) return sanitizeUrl(cover) || null;
  }
  return null;
}

/** Two initials for an actor mark. */
/** A user with no picture is drawn by the first letter of their name — one letter, everywhere. */
export function initials(name: string): string {
  const cleaned = name.replace(/^NB-User-#?/i, "").replace(/[^\p{L}\p{N}\s-]/gu, " ").trim();
  const first = Array.from(cleaned)[0];
  return first ? first.toLocaleUpperCase() : "?";
}

export type TileOwner = { name?: string | null; username?: string | null; blue_check?: number | null; profile?: unknown; avatar?: string | null } | null | undefined;

export type TileCollection = {
  id: number;
  name?: string | null;
  contract_address?: string | null;
  network?: number | string | null;
  logo?: string | null;
  banner?: string | null;
  meta?: unknown;
  likes?: { count?: number | null } | null;
  user?: TileOwner;
};

export type TileAuction = {
  id: number;
  title?: string | null;
  slug?: string | null;
  image?: string | null;
  start_price?: string | number | null;
  max_price?: string | number | null;
  expire_time?: string | null;
  bids?: unknown;
  currency?: { symbol?: string | null } | null;
  creator?: TileOwner;
  user?: TileOwner;
};

export type TileGalleryModel = { id: number; data?: { image?: string | null; name?: string | null } | null };

export type TileGallery = {
  id: number;
  title?: string | null;
  slug?: string | null;
  image?: string | null;
  owner?: TileOwner;
  models?: { nfts?: TileGalleryModel[]; collections?: TileGalleryModel[] } | null;
};

export const bidCount = (a: TileAuction) => (Array.isArray(a.bids) ? a.bids.length : 0);
export const hasEnded = (iso?: string | null) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t <= Date.now();
};

/** Up to four artwork thumbnails held by a gallery, for its cover mosaic. */
export function galleryThumbs(g: TileGallery): string[] {
  const models = [...(g.models?.nfts ?? []), ...(g.models?.collections ?? [])];
  const urls: string[] = [];
  for (const m of models) {
    const u = sanitizeUrl(m?.data?.image);
    if (u && !urls.includes(u)) urls.push(u);
    if (urls.length === 4) break;
  }
  return urls;
}

/**
 * The creator line on a card: avatar, name, and a link straight to that
 * artist's page.
 *
 * It sits INSIDE a card whose title link covers the whole card, which is why
 * `.v2-creator` carries a z-index — see the stretched-link note in `v2.css`.
 * Without a `username` there is nowhere to go, so it renders as plain text
 * rather than a link to a 404.
 */
export function Creator({ owner, lang, fallback }: { owner: TileOwner; lang: "fa" | "en"; fallback: string }) {
  const name = owner?.name || owner?.username || fallback;
  const src = avatarOf(owner);
  const inner = (
    <>
      {src ? (
        <Image src={src} alt="" width={18} height={18} />
      ) : (
        <span className="v2-creator-ini" aria-hidden>
          {initials(name)}
        </span>
      )}
      <span>{name}</span>
      {owner?.blue_check ? <BadgeCheck aria-label="verified" /> : null}
    </>
  );

  if (!owner?.username) return <span className="v2-creator">{inner}</span>;
  return (
    <Link className="v2-creator v2-focus" href={`/${lang}/artists/${owner.username}`}>
      {inner}
    </Link>
  );
}

const copy = (isFa: boolean) => (fa: string, en: string) => (isFa ? fa : en);

export function CollectionTile({ c, lang, sizes = "216px" }: { c: TileCollection; lang: "fa" | "en"; sizes?: string }) {
  const isFa = lang === "fa";
  const t = copy(isFa);
  const num = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const persian = isFa ? faName(c.meta) : null;
  const likes = c.likes?.count ?? 0;
  return (
    <article className="v2-tile v2-coll">
      {/* network_id rides along so the page hits /collections/V2 directly — the
          same link the /collection list's cards build. */}
      <Link className="v2-tile-link" href={`/${lang}/collection/${c.contract_address}${c.network != null && c.network !== "" ? `?network_id=${c.network}` : ""}`}>
        <span className="v2-sr-only">{persian || c.name}</span>
      </Link>
      <div className="v2-coll-plate">
        <Image
          src={sanitizeUrl(c.banner) || sanitizeUrl(c.logo) || TILE_PLACEHOLDER}
          alt=""
          fill
          loading="lazy"
          sizes={sizes}
          style={{ objectFit: "cover" }}
        />
        <Image
          className="v2-coll-logo"
          src={sanitizeUrl(c.logo) || TILE_PLACEHOLDER}
          alt=""
          width={100}
          height={100}
          loading="lazy"
        />
        {likes > 0 ? <span className="v2-coll-count">♥ {num.format(likes)}</span> : null}
      </div>
      <div className="v2-tile-body">
        <span className="v2-tile-name">{persian || c.name}</span>
        <div className="v2-tile-meta">
          <Creator owner={c.user} lang={lang} fallback={t("ناشناس", "Unknown")} />
          <span
            className="v2-chip v2-chip--chain"
            data-chain={String(c.network) === "3" ? "polygon" : "ethereum"}
          >
            {chainName(String(c.network) === "3" ? "polygon" : "ethereum", isFa)}
          </span>
        </div>
      </div>
    </article>
  );
}

export function AuctionTile({ a, lang, sizes = "244px" }: { a: TileAuction; lang: "fa" | "en"; sizes?: string }) {
  const isFa = lang === "fa";
  const t = copy(isFa);
  const num = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  return (
    <article className="v2-tile v2-auction">
      <Link className="v2-tile-link v2-focus" href={`/${lang}/auctions/${a.slug}`} aria-label={a.title || t("حراج", "Auction")}>
        <span className="v2-sr-only">{a.title || t("حراج", "Auction")}</span>
      </Link>
      <div className="v2-tile-media">
        <Image
          src={sanitizeUrl(a.image) || TILE_PLACEHOLDER}
          alt={a.title || ""}
          fill
          loading="lazy"
          sizes={sizes}
          style={{ objectFit: "cover" }}
        />
        {/* On the plate, not in the body: the dot's pulse ring grows
            7px and was being clipped by the body's edge. */}
        <span className="v2-live-badge" data-state={hasEnded(a.expire_time) ? "ended" : "live"}>
          <span className="v2-live-dot" aria-hidden />
          {hasEnded(a.expire_time) ? t("پایان یافته", "Ended") : t("در جریان", "Live")}
        </span>
      </div>
      <div className="v2-tile-body">
        <span className="v2-tile-name">{a.title || t("حراج", "Auction")}</span>
        <div className="v2-auction-creator">
          <span>{t("سازنده", "Creator")}</span>
          {/* `/auctions` names the creator without a picture: it is looked up once per person. */}
          <Creator owner={a.creator || a.user} lang={lang} fallback={t("ناشناس", "Unknown")} />
        </div>
        <div className="v2-auction-row">
          <span className="v2-auction-k">{t("قیمت پایه", "Starting price")}</span>
          {/* The same plate the Buy-it-now cards use. A starting
              price is the same kind of fact and was set three steps
              quieter than one. */}
          <span className="v2-price">
            <bdi>{a.start_price ? num.format(Number(a.start_price)) : "—"}</bdi>
            <small>{ticker(a.currency?.symbol)}</small>
          </span>
        </div>
        <div className="v2-auction-row">
          <span className="v2-auction-k">
            {bidCount(a) > 0
              ? `${num.format(bidCount(a))} ${t("پیشنهاد", "bids")}`
              : t("هنوز پیشنهادی نیست", "No bids yet")}
          </span>
          <Countdown iso={a.expire_time} isFa={isFa} fallback={t("—", "—")} today />
        </div>
      </div>
    </article>
  );
}

export function GalleryTile({ g, lang, sizes = "150px" }: { g: TileGallery; lang: "fa" | "en"; sizes?: string }) {
  const isFa = lang === "fa";
  const t = copy(isFa);
  const num = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const held = (g.models?.nfts?.length ?? 0) + (g.models?.collections?.length ?? 0);
  const thumbs = galleryThumbs(g);
  return (
    <article className="v2-tile v2-gal">
      <Link className="v2-tile-link" href={`/${lang}/galleries/${g.slug}`}>
        <span className="v2-sr-only">{g.title}</span>
      </Link>
      {/* THE COVER IS THE CONTENTS, NOT A COVER IMAGE.
          A gallery is a selection of work, so the card shows the
          work: up to four of the pieces it actually holds, in a
          mosaic. `g.image` is an owner-uploaded banner that says
          nothing about what is inside, and it is the fallback only
          when the gallery holds nothing yet. */}
      <div className="v2-tile-media v2-gal-mosaic" data-n={Math.min(thumbs.length, 4) || 1}>
        {(thumbs.length > 0 ? thumbs : [sanitizeUrl(g.image) || TILE_PLACEHOLDER]).map((src, i) => (
          <span className="v2-gal-cell" key={`${g.id}-${i}`}>
            <Image
              src={src}
              alt=""
              fill
              loading="lazy"
              sizes={sizes}
              style={{ objectFit: "cover" }}
            />
          </span>
        ))}
      </div>
      <div className="v2-tile-body">
        <span className="v2-tile-name">{g.title}</span>
        <div className="v2-tile-meta">
          <Creator owner={g.owner} lang={lang} fallback={t("ناشناس", "Unknown")} />
          {held > 0 ? (
            <span className="v2-chip v2-chip--gold">
              {num.format(held)} {t("اثر", held === 1 ? "item" : "items")}
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
}
